import * as React from 'react'

import { Badge } from '../../../components/ui/badge'
import { cn } from '../../../lib/utils'
import { ConfidenceValue } from '../../PlantPage/entity/ConfidenceValue'

export const DEFAULT_ACCEPT_NOTE = 'confidence manually confirmed'

/**
 * Shared Settings row shell for graph-curation panels (flagged nodes, low
 * confidence, review queue). Renders identity, optional confidence / reason,
 * and a primary action with an inline note composer. Delete is optional.
 */
export function EntityReviewRow({
  title,
  subtitle,
  badges = [],
  confidence,
  reason,
  primaryLabel,
  composerLabel,
  notePlaceholder,
  confirmLabel,
  confirmingLabel,
  isComposing,
  note,
  onNoteChange,
  isSubmitting,
  submitError,
  onOpenPrimary,
  onCancelPrimary,
  onConfirmPrimary,
  onDelete
}: {
  title: string
  subtitle?: string
  badges?: string[]
  confidence?: number
  reason?: React.ReactNode
  primaryLabel: string
  composerLabel: string
  notePlaceholder?: string
  confirmLabel: string
  confirmingLabel: string
  isComposing: boolean
  note: string
  onNoteChange: (next: string) => void
  isSubmitting: boolean
  submitError: string
  onOpenPrimary: () => void
  onCancelPrimary: () => void
  onConfirmPrimary: () => void
  onDelete?: () => void
}): React.ReactElement {
  const canConfirm = note.trim().length > 0 && !isSubmitting

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-line bg-card px-3 py-2.5">
      <div className="flex items-start gap-2">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate text-sm font-medium text-ink">{title}</span>
            {badges.map((badge) => (
              <Badge key={badge} variant="outline">
                {badge}
              </Badge>
            ))}
            {confidence !== undefined ? (
              <ConfidenceValue value={confidence} compact className="ml-auto" />
            ) : null}
          </div>
          {subtitle ? <span className="truncate text-xs text-ink-3">{subtitle}</span> : null}
        </div>
        {!isComposing ? (
          <div className="flex shrink-0 items-center gap-1.5">
            <button
              type="button"
              onClick={onOpenPrimary}
              className="inline-flex cursor-pointer items-center rounded-md border border-st-run/40 bg-st-run-bg px-2.5 py-1 text-xs font-medium text-st-run transition-colors duration-150 ease-out hover:bg-st-run-bg/70"
            >
              {primaryLabel}
            </button>
            {onDelete ? (
              <button
                type="button"
                onClick={onDelete}
                className="inline-flex cursor-pointer items-center rounded-md border border-st-down/40 bg-st-down-bg px-2.5 py-1 text-xs font-medium text-st-down transition-colors duration-150 ease-out hover:bg-st-down-bg/70"
              >
                Delete
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      {reason}

      {isComposing ? (
        <div className="flex flex-col gap-2 rounded-md border border-st-run/40 bg-st-run-bg/20 p-3">
          <label className="text-11 font-medium uppercase tracking-wide text-st-run">
            {composerLabel}
          </label>
          <textarea
            autoFocus
            rows={2}
            value={note}
            onChange={(e) => onNoteChange(e.target.value)}
            placeholder={notePlaceholder}
            className="w-full resize-y rounded-md border border-line bg-bg px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-st-run focus:outline-none focus:ring-2 focus:ring-st-run/30"
          />
          {submitError ? (
            <div role="alert" className="rounded-md bg-st-down-bg px-3 py-2 text-xs text-st-down">
              {submitError}
            </div>
          ) : null}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onConfirmPrimary}
              disabled={!canConfirm}
              className={cn(
                'inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs font-semibold',
                'transition-[background-color,border-color,opacity] duration-150 ease-out',
                canConfirm
                  ? 'cursor-pointer border-st-run bg-st-run text-bg hover:opacity-90'
                  : 'cursor-not-allowed border-line bg-bg-sunk text-ink-3'
              )}
            >
              {isSubmitting ? confirmingLabel : confirmLabel}
            </button>
            <button
              type="button"
              onClick={onCancelPrimary}
              className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-line bg-bg px-2.5 py-1 text-xs text-ink-2 hover:bg-bg-sunk"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}
    </div>
  )
}
